/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { DOCUMENT } from '@angular/common';
import { isElementNull, isStringNullOrEmpty } from '../../../../utils';
import { IPlace } from '../../../geofence/models';
import { ResetGeofenceState, ShowExistingGeofenceForEdit } from '../../../geofence/store';
import { UpdateReplayDatePickerSelectionMode } from '../../../replay/store';
import { StartNonPanicNotifications, StopNonPanicNotifications } from '../../components';
import {
  EAssetDateRangeSelectionMode,
  EPlaceBalloonType,
  ESidePanelContent,
  EViewReplayType,
  ICoordinates,
  IEditAssetParams,
  IEditDriverParams,
  IEditPlaceParams,
  IEditVehicleParams,
  IFindDirectionParams,
  IFindNearestParams,
  ILatLng,
  IPlaceBalloonResponse,
  IRunReportParams,
  IVehiclePlot,
  IViewGarminWindowParams,
  IViewMetricsParams,
  IViewReplayParameters,
  IWorkOrderParams,
  LatLng,
  LatLngBounds
} from '../../models';
import { ShapeCoordinates } from '../../models/shapesCoordinates.interface';
import { delayedRetry } from '../../operators';
import { BrowserLocationService, GoogleMapsService, ModalWindowService, PlacesHttpService, WindowResizeService } from '../../services';
import {
  ChangeMapBounds,
  CloseBalloon,
  CloseReplayOnLiveMap,
  CloseSidePanel,
  EBalloonInfoActions,
  ELayoutActions,
  OpenGeofencePanel,
  OpenReplayOnLiveMap,
  RemoveIndexLoaderIcon,
  RemoveZoomToMarker,
  SetPageTitle,
  SetReplayPageTitleFromIdAndType,
  ToggleFullscreen,
  TriggerCreateGeofence,
  TriggerCreateGeofenceForVehicle as TriggerCreateGeofenceOnVehicle,
  TriggerDriverDispatch,
  TriggerEditAsset,
  TriggerEditDriver,
  TriggerEditPlace,
  TriggerEditVehicle,
  TriggerFindNearest,
  TriggerGarmin,
  TriggerGetDirectionsFrom,
  TriggerGetDirectionsFromVehicle,
  TriggerGetDirectionsTo,
  TriggerGetDirectionsToVehicle,
  TriggerRunReport,
  TriggerTextview,
  TriggerViewGarminWindow,
  TriggerViewMetrics,
  TriggerViewReplay,
  TriggerWorkOrderDetails,
  TriggerWorkOrderStatusHistory,
  TryUpdateZoomToMarker,
  UpdateAnimationState,
  UpdateIsAppAnimationsEnabled,
  UpdateIsFullscreen,
  UpdateIsStreetViewOpen,
  UpdateViewReplayIdAndType,
  UpdateZoomToLocation,
  UpdateZoomToMarker,
  ZoomToLocation
} from '../actions';
import { CloseInsightsPanel, OpenInsightsPanel } from '../actions/insights-panel.actions';
import {
  getAnimationState,
  getAssetPlots,
  getIsFullscreen,
  getIsStreetViewOpen,
  getMapBounds,
  getReplayLayout,
  getSidePanelContent,
  getVehiclePlots,
  getZoomToLocation
} from '../selectors';
import { EMapAnimationState, IAppState } from '../state';
import { trimOutlet } from '../../utils';

@Injectable()
export class LayoutEffects {
  private readonly _placesPath = '/places/edit/default.aspx';
  private readonly _findNearestPath = '/map/findnearest/default.aspx';

  public zoomToLocation$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<ZoomToLocation>(ELayoutActions.ZoomToLocation),
      withLatestFrom(this._store.pipe(select(getZoomToLocation)), (_action, state) => state),
      map((locationToZoom: ILatLng) => new UpdateZoomToLocation(locationToZoom))
    )
  );

  public tryUpdateZoomToMarker$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<TryUpdateZoomToMarker>(ELayoutActions.TryUpdateZoomToMarker),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getIsStreetViewOpen))),
      switchMap(([params, isStreetViewOpen]) => {
        if (isStreetViewOpen) {
          return this._googleMapsService.isStreetViewAvailable(new google.maps.LatLng(params.latLng.lat, params.latLng.lng)).pipe(
            filter(isStreetViewAvailable => isStreetViewAvailable),
            map(() => new UpdateZoomToMarker(params))
          );
        }
        return of(new UpdateZoomToMarker(params));
      })
    )
  );

  public toggleFullscreen$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<ToggleFullscreen>(ELayoutActions.ToggleFullscreen),
      withLatestFrom(this._store.pipe(select(getIsFullscreen)), (_action, state) => state),
      map((isFullscreen: boolean) => new UpdateIsFullscreen(!isFullscreen))
    )
  );

  public triggerTextview$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerTextview>(ELayoutActions.TriggerTextview),
        tap(() => {
          const url = trimOutlet(this._document.defaultView.location.href);
          // Remove outlet path from URL if it exists (e.g., remove /(outlet:path))
          this._modalWindowService.openInANewWindowSpecificSize(url + 'textview', 'textview');
        })
      ),
    { dispatch: false }
  );

  public triggerGarmin$: Observable<Action> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerGarmin>(ELayoutActions.TriggerGarmin),
        tap(() => {
          this._modalWindowService.openWithPostCentered('/garmin/default.aspx', 'garmin');
        })
      ),
    { dispatch: false }
  );

  public triggerDriverDispatch$: Observable<Action> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerDriverDispatch>(ELayoutActions.TriggerDriverDispatch),
        tap(() => {
          this._modalWindowService.openWithPostCentered('/routing/stops/default.aspx', 'dispatch');
        })
      ),
    { dispatch: false }
  );

  public triggerCreateInlineGeofence$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<TriggerCreateGeofence>(ELayoutActions.TriggerCreateGeofence),
      switchMap(action => {
        const latLng: LatLng = new LatLng(action.payload.lat, action.payload.lng);
        const actions: Action[] = [new ResetGeofenceState(), new OpenGeofencePanel(latLng)];
        return actions;
      })
    )
  );

  public triggerEditInlineGeofence$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<TriggerEditPlace>(ELayoutActions.TriggerEditPlace),
      map((action: TriggerEditPlace) => action.payload),
      filter(payload => payload.type === EPlaceBalloonType.Geofence || payload.type === EPlaceBalloonType.Hotspot),
      switchMap((payload: IEditPlaceParams) =>
        this._placesHttpService.getPlaceBalloon(payload.id, payload.type).pipe(
          delayedRetry(),
          catchError(() => of(null))
        )
      ),
      filter((response: IPlaceBalloonResponse | null) => !isElementNull(response)),
      switchMap(response => [
        new OpenGeofencePanel(new LatLng(response.center.Latitude, response.center.Longitude)),
        new ShowExistingGeofenceForEdit(this._mapVehiclePlotResponseToModel(response))
      ])
    )
  );

  public triggerCreateVehicleInlineGeofence$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<TriggerCreateGeofenceOnVehicle>(ELayoutActions.TriggerCreateGeofenceOnVehicle),
      map((action: TriggerCreateGeofenceOnVehicle) => action.payload),
      withLatestFrom(this._store.pipe(select(getVehiclePlots))),
      map(([vehicleId, vehiclePlots]) => {
        const vehiclePlot = vehiclePlots.find(plot => plot.id === vehicleId);
        return new OpenGeofencePanel(new LatLng(vehiclePlot.coordinates.lat, vehiclePlot.coordinates.lng));
      })
    )
  );

  public triggerFindNearest$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerFindNearest>(ELayoutActions.TriggerFindNearest),
        map((action: TriggerFindNearest) => action.payload),
        tap((params: IFindNearestParams) => {
          this._modalWindowService.openWithPostCentered(this._findNearestPath, 'findNearest', params, undefined, true);
        })
      ),
    { dispatch: false }
  );

  public triggerViewMetrics$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerViewMetrics>(ELayoutActions.TriggerViewMetrics),
        map((action: TriggerViewMetrics) => action.payload),
        tap((params: IViewMetricsParams) => {
          this._modalWindowService.openWithPostCentered(this._placesPath, 'Places', params);
        })
      ),
    { dispatch: false }
  );

  public triggerGetDirectionsFrom$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerGetDirectionsFrom>(ELayoutActions.TriggerGetDirectionsFrom),
        map(action => action.payload),
        tap((coordinates: ILatLng) => {
          const params: IFindDirectionParams = this._createFindDirectionParameters(coordinates, true);
          this._modalWindowService.openWithPostCentered(this._findNearestPath, 'directions', params);
        })
      ),
    { dispatch: false }
  );

  public triggerGetDirectionsTo$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerGetDirectionsTo>(ELayoutActions.TriggerGetDirectionsTo),
        map(action => action.payload),
        tap((coordinates: ILatLng) => {
          const params: IFindDirectionParams = this._createFindDirectionParameters(coordinates, false);
          this._modalWindowService.openWithPostCentered(this._findNearestPath, 'directions', params);
        })
      ),
    { dispatch: false }
  );

  public triggerGetDirectionsFromVehicle$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerGetDirectionsFromVehicle>(ELayoutActions.TriggerGetDirectionsFromVehicle),
        map((action: TriggerGetDirectionsFromVehicle) => action.payload),
        withLatestFrom(this._store.pipe(select(getVehiclePlots))),
        tap(([vehicleId, vehiclePlots]: [number, IVehiclePlot[]]) => {
          const vehiclePlot = vehiclePlots.find(plot => plot.id === vehicleId);

          const params: IFindDirectionParams = this._createFindDirectionParameters(vehiclePlot.coordinates, true);
          this._modalWindowService.openWithPostCentered(this._findNearestPath, 'directions', params);
        })
      ),
    { dispatch: false }
  );

  public triggerGetDirectionsToVehicle$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerGetDirectionsToVehicle>(ELayoutActions.TriggerGetDirectionsToVehicle),
        map((action: TriggerGetDirectionsToVehicle) => action.payload),
        withLatestFrom(this._store.pipe(select(getVehiclePlots))),
        tap(([vehicleId, vehiclePlots]: [number, IVehiclePlot[]]) => {
          const vehiclePlot = vehiclePlots.find(plot => plot.id === vehicleId);

          const params: IFindDirectionParams = this._createFindDirectionParameters(vehiclePlot.coordinates, false);
          this._modalWindowService.openWithPostCentered(this._findNearestPath, 'directions', params);
        })
      ),
    { dispatch: false }
  );

  public triggerViewReplay$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerViewReplay>(ELayoutActions.TriggerViewReplay),
        map(action => action.payload),
        withLatestFrom(this._store.pipe(select(getMapBounds)), this._store.pipe(select(getIsFullscreen))),
        tap(([viewReplayParameters, mapBounds, isFullscreen]) => {
          const replayOnLiveMapParams: IViewReplayParameters = {
            ...viewReplayParameters,
            isFullscreen,
            lastBounds: mapBounds
          };

          this._store.dispatch(new OpenReplayOnLiveMap(replayOnLiveMapParams));
        })
      ),
    { dispatch: false }
  );

  public onTriggerViewReplayCloseSidePanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<TriggerViewReplay>(ELayoutActions.TriggerViewReplay),
      withLatestFrom(this._store.pipe(select(getSidePanelContent))),
      filter(([, sidePanelContent]) => sidePanelContent === ESidePanelContent.Geofence),
      map(() => new CloseSidePanel())
    )
  );

  public onOpenCloseReplayOnLiveMapDisableAnimations$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseReplayOnLiveMap | OpenReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap, ELayoutActions.OpenReplayOnLiveMap),
      withLatestFrom(this._windowResize.isMobileOrTablet$),
      filter(
        ([action, isMobileOrTablet]) =>
          isMobileOrTablet || (action.type === ELayoutActions.OpenReplayOnLiveMap && action.payload?.disableAnimation)
      ),
      map(() => new UpdateIsAppAnimationsEnabled(false))
    )
  );

  public onOpenReplayOnLiveMap$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenReplayOnLiveMap>(ELayoutActions.OpenReplayOnLiveMap),
      map(action => action.payload),
      switchMap(params => [
        new CloseInsightsPanel(),
        new UpdateAnimationState(EMapAnimationState.overlayHidden),
        new UpdateIsFullscreen(true),
        new StopNonPanicNotifications(),
        new UpdateIsStreetViewOpen(false),
        new CloseBalloon(),
        new SetReplayPageTitleFromIdAndType({ id: params.id, type: params.type })
      ])
    )
  );

  public setToCustomDateSelectionWhenOpenedExternally$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenReplayOnLiveMap>(ELayoutActions.OpenReplayOnLiveMap),
      filter(action => action.payload.isOpenedFromExternal),
      map(() => new UpdateReplayDatePickerSelectionMode(EAssetDateRangeSelectionMode.Custom))
    )
  );

  public onCloseReplayOnLiveMapAnimationTransition$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
      switchMap(() =>
        this._store.pipe(
          select(getAnimationState),
          filter(state => state === EMapAnimationState.overlayHidden),
          take(1)
        )
      ),
      withLatestFrom(this._store.pipe(select(getReplayLayout))),
      switchMap(([, replayLayout]) => [
        new UpdateAnimationState(EMapAnimationState.default),
        replayLayout.viewReplayParams.isFullscreen ? new UpdateIsFullscreen(true) : new UpdateIsFullscreen(false)
      ])
    )
  );

  public onCloseReplayOnLiveMapRestoreBounds$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
      switchMap(() =>
        this._store.pipe(
          select(getAnimationState),
          filter(state => state === EMapAnimationState.default),
          take(1)
        )
      ),
      withLatestFrom(this._store.pipe(select(getReplayLayout))),
      delay(1000),
      switchMap(([, replayLayout]) => [
        new ChangeMapBounds(LatLngBounds.toLatLngs(replayLayout.viewReplayParams.lastBounds)),
        new OpenInsightsPanel()
      ])
    )
  );

  public onCloseReplayTryAndCloseStreetView$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
      withLatestFrom(this._store.pipe(select(getIsStreetViewOpen))),
      filter(([, isStreetViewOpen]) => isStreetViewOpen),
      map(() => new UpdateIsStreetViewOpen(false))
    )
  );

  public onCloseReplayStartNonPanicNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
      map(() => new StartNonPanicNotifications())
    )
  );

  public onOpenReplayBackButtonBehaviour$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<OpenReplayOnLiveMap>(ELayoutActions.OpenReplayOnLiveMap),
        withLatestFrom(this._windowResize.isDesktop$, this._store.pipe(select(getReplayLayout))),
        filter(([, isDesktop, replayLayout]) => isDesktop && !replayLayout.viewReplayParams?.onCloseNavigateToHome),
        tap(() => this._browserLocationService.onBackButtonCloseReplay())
      ),
    { dispatch: false }
  );

  public onCloseReplayBackButtonBehaviour$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
        withLatestFrom(this._windowResize.isDesktop$, this._store.pipe(select(getReplayLayout))),
        filter(([, isDesktop, replayLayout]) => isDesktop && !replayLayout.viewReplayParams?.onCloseNavigateToHome),
        tap(() => this._browserLocationService.navigateToFirstPage())
      ),
    { dispatch: false }
  );

  public onCloseReplayNavigateToHome$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
        withLatestFrom(this._store.pipe(select(getReplayLayout))),
        filter(([, replayLayout]) => replayLayout.viewReplayParams?.onCloseNavigateToHome),
        tap(() => (this._document.defaultView.location.href = '/'))
      ),
    { dispatch: false }
  );

  public onFullscreen$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsFullscreen>(ELayoutActions.UpdateIsFullscreen),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getAnimationState))),
      switchMap(([isFullscreen, animationState]) => {
        if (isFullscreen && animationState === EMapAnimationState.default) {
          return [new UpdateAnimationState(EMapAnimationState.fullscreen)];
        }
        if (!isFullscreen && animationState === EMapAnimationState.fullscreen) {
          return [new UpdateAnimationState(EMapAnimationState.default)];
        }
        return [];
      })
    )
  );

  public triggerRunReport$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerRunReport>(ELayoutActions.TriggerRunReport),
        map((action: TriggerRunReport) => action.payload),
        tap((params: IRunReportParams) => {
          const message = {
            REPORT_CMD: 'run_report_from_maps',
            REPORT_ARGS: params
          };
          this._modalWindowService.openNewTabWithMessage(`/reports/report-list`, `report_${Date.now()}`, message);
        })
      ),
    { dispatch: false }
  );

  public triggerEditDriver$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerEditDriver>(ELayoutActions.TriggerEditDriver),
        map((action: TriggerEditDriver) => action.payload),
        tap((params: IEditDriverParams) => {
          this._modalWindowService.openWithPost('/admin/drivers/default.aspx', 'adminDriver', params);
        })
      ),
    { dispatch: false }
  );

  public triggerEditVehicle$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerEditVehicle>(ELayoutActions.TriggerEditVehicle),
        map((action: TriggerEditVehicle) => action.payload),
        tap((params: IEditVehicleParams) => {
          this._modalWindowService.openWithPost('/admin/vehicles/default.aspx', 'adminVehicle', params);
        })
      ),
    { dispatch: false }
  );

  public triggerEditAsset$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerEditAsset>(ELayoutActions.TriggerEditAsset),
        map((action: TriggerEditAsset) => action.payload),
        tap((params: IEditAssetParams) => {
          this._modalWindowService.redirectWithPost('/admin/assets/default.aspx', '', params);
        })
      ),
    { dispatch: false }
  );

  public triggerViewGarminWindow$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerViewGarminWindow>(ELayoutActions.TriggerViewGarminWindow),
        map((action: TriggerViewGarminWindow) => action.payload),
        tap((params: IViewGarminWindowParams) => {
          this._modalWindowService.openWithPostCentered('/garmin/default.aspx', 'Garmin', params);
        })
      ),
    { dispatch: false }
  );

  public triggerEditPlace$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerEditPlace>(ELayoutActions.TriggerEditPlace),
        map((action: TriggerEditPlace) => action.payload),
        filter(payload => payload.type !== EPlaceBalloonType.Geofence && payload.type !== EPlaceBalloonType.Hotspot),
        tap((params: IEditPlaceParams) => {
          this._modalWindowService.openWithPostCentered(this._placesPath, 'Places', params);
        })
      ),
    { dispatch: false }
  );

  public triggerWorkOrderDetails$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerWorkOrderDetails>(ELayoutActions.TriggerWorkOrderDetails),
        map((action: TriggerWorkOrderDetails) => action.payload),
        tap((params: IWorkOrderParams) => {
          this._modalWindowService.openWithPostCentered('/map/workorder/default.aspx', 'directions', params);
        })
      ),
    { dispatch: false }
  );

  public triggerWorkOrderStatusHistory$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<TriggerWorkOrderStatusHistory>(ELayoutActions.TriggerWorkOrderStatusHistory),
        map((action: TriggerWorkOrderStatusHistory) => action.payload),
        tap((params: IWorkOrderParams) => {
          this._modalWindowService.openWithPostCentered('/workorder/statushistory/default.aspx', 'directions', params);
        })
      ),
    { dispatch: false }
  );

  public setReplayPageTitleForUpdate$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateViewReplayIdAndType>(ELayoutActions.UpdateViewReplayIdAndType),
      map(action => new SetReplayPageTitleFromIdAndType({ id: action.payload.id, type: action.payload.type }))
    )
  );

  public setReplayPageTitleFromIdAndType$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<SetReplayPageTitleFromIdAndType>(ELayoutActions.SetReplayPageTitleFromIdAndType),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getVehiclePlots)), this._store.pipe(select(getAssetPlots))),
      map(([{ id, type }, vehiclePlots, assetPlots]) =>
        type === EViewReplayType.Vehicle
          ? vehiclePlots.find(plot => plot.id === id)?.vehicleName
          : assetPlots.find(plot => plot.id === id)?.name
      ),
      filter(name => !isStringNullOrEmpty(name)),
      map(name => new SetPageTitle(`${$localize`:@@S_REPLAY:Replay`} ${name}`))
    )
  );

  public setPageTitleOnReplayClose$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseReplayOnLiveMap>(ELayoutActions.CloseReplayOnLiveMap),
      map(() => new SetPageTitle($localize`:@@S_SITE_PAGE_TITLE:Verizon Connect | GPS Fleet Tracking System`))
    )
  );

  public setPageTitle$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<SetPageTitle>(ELayoutActions.SetPageTitle),
        map(action => action.payload),
        tap(title => {
          setTimeout(() => {
            // The title service seems to not update sometimes, using a timeout fixes it
            this._titleService.setTitle(title);
          }, 0);
        })
      ),
    { dispatch: false }
  );

  public onCloseBalloonRemoveZoomToMarker$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseBalloon>(EBalloonInfoActions.CloseBalloon),
      map(() => new RemoveZoomToMarker())
    )
  );

  public removeIndexLoaderIcon$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<RemoveIndexLoaderIcon>(ELayoutActions.RemoveIndexLoaderIcon),
        tap(() => {
          this._document.querySelector('#index-loading-container')?.remove();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<IAppState>,
    private readonly _modalWindowService: ModalWindowService,
    private readonly _googleMapsService: GoogleMapsService,
    private readonly _windowResize: WindowResizeService,
    private readonly _browserLocationService: BrowserLocationService,
    private readonly _titleService: Title,
    private readonly _placesHttpService: PlacesHttpService,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  private _createFindDirectionParameters(coordinates: ILatLng, isFrom: boolean): IFindDirectionParams {
    return {
      m: '1',
      dp: `(${coordinates.lat}, ${coordinates.lng})`,
      from: isFrom,
      optionalVehicleID: -1
    };
  }

  private _mapVehiclePlotResponseToModel(placeBalloon: IPlaceBalloonResponse): IPlace {
    return {
      ...placeBalloon,
      addressComponents: placeBalloon.addressComponents,
      fulladdress: placeBalloon.fulladdress,
      id: placeBalloon.id,
      name: placeBalloon.name,
      desc: placeBalloon.desc,
      catname: placeBalloon.catname,
      catid: placeBalloon.catid,
      groupids: placeBalloon.groupids,
      cname: placeBalloon.cname,
      cno: placeBalloon.cno,
      clientid: placeBalloon.clientid,
      smap: placeBalloon.smap,
      saddr: placeBalloon.saddr,
      shapetype: placeBalloon.shapetype,
      shapecoords: this._mapShapeCoords(placeBalloon.shapecoords),
      radius: placeBalloon.radius,
      center: placeBalloon.center,
      type: placeBalloon.type,
      address: {
        FormattedAddress: '',
        AddressLine1: '',
        AddressLine2: '',
        City: '',
        State: '',
        PostalCode: '',
        Country: 0,
        Latitude: 0,
        Longitude: 0,
        AddressComponents: ''
      },
      swbound: undefined,
      nebound: undefined,
      caticon: '',
      catcolor: placeBalloon.catcolor,
      editlabel: ''
    };
  }

  private _mapShapeCoords(shapecoords: { Points: ShapeCoordinates[] }[]): { Points: ICoordinates[] }[] {
    return shapecoords?.map(shape => ({
      Points: shape?.Points?.map(coord => ({
        Latitude: coord?._latitude,
        Longitude: coord?._longitude
      }))
    }));
  }
}
